/**
 * @fileoverview A page exported as its own source: a file, never a second event.
 *
 * page-format.md: "A page can be exported *as* markdown, which is a file, not
 * a second event." Canonical storage is the source, so what leaves is editable
 * source, the thing "export everything and host it yourself" promises.
 *
 * A MARKDOWN PAGE gains a front-matter header with what lives in the event
 * rather than the body (title, summary, address, theme, first publication), so
 * the file stands alone. Each value is a JSON string, which is a valid YAML
 * double-quoted scalar, so no title can break out of its line.
 *
 * AN HTML PAGE LEAVES BYTE FOR BYTE. Anything added in front of a document
 * changes how a browser parses it, so it gets no header.
 */

import type { PageIndex } from './indexEvent';

import { encodeAddress } from '@/services/nostr/pageLinks';

export interface ExportedPage {
  filename: string;
  mime: 'text/markdown' | 'text/html';
  text: string;
}

function baseName(index: PageIndex): string {
  const slug = index.title
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return slug || index.address.identifier;
}

export function exportPage(index: PageIndex, body: string): ExportedPage {
  const name = baseName(index);
  if (index.format === 'html') return { filename: `${name}.html`, mime: 'text/html', text: body };

  const lines = [`title: ${JSON.stringify(index.title)}`];
  if (index.summary) lines.push(`summary: ${JSON.stringify(index.summary)}`);
  lines.push(`address: ${JSON.stringify(encodeAddress(index.address))}`);
  if (index.themeAddress) lines.push(`theme: ${JSON.stringify(index.themeAddress)}`);
  if (index.publishedAt !== null) lines.push(`published_at: ${index.publishedAt}`);

  return {
    filename: `${name}.md`,
    mime: 'text/markdown',
    text: `---\n${lines.join('\n')}\n---\n${body}`,
  };
}

/** Hand the file to the browser as a download. */
export function downloadExport(file: ExportedPage): void {
  const url = URL.createObjectURL(new Blob([file.text], { type: `${file.mime};charset=utf-8` }));
  const link = document.createElement('a');
  link.href = url;
  link.download = file.filename;
  link.click();
  // After the click has been handled, not before it.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
